/**
 * Resolves which API client + module path serve the component-rules
 * endpoints for the runtime rules hooks (`useComponentRules`,
 * `useInactiveComponentRules`, `useUpdateInactiveRules`).
 *
 * The rules endpoints are mounted per module on Origami
 * (`/api/<module>/component-rules…`), each behind that module's own Sanctum
 * token. A module tree mounts an `IntegratedComponentProvider` carrying its
 * authenticated `apiClient` and its `module` slug, so the hooks read both from
 * that context instead of being hard-wired to claimflow:
 *
 * - **Provider present** (and, when a component id is known, the component's
 *   module token equals the provider's `module`) ⇒ the provider's client and
 *   `/<module>/component-rules…` — e.g. extranet pages hit
 *   `/extranet/component-rules` with `extranetApiClient`.
 * - **No provider, or a component of another module** (admin previews of a
 *   claimflow card, a co-located wrapper rendered inside the admin tree) ⇒ the
 *   historical claimflow client and `/claimflow/component-rules…`.
 *
 * Claimflow pages mount `IntegratedComponentProvider` with
 * `claimflowApiClient` + `module="claimflow"`, so both branches resolve to the
 * exact same client and URL as before the decoupling: the claimflow runtime is
 * byte-identical.
 */

import { useMemo } from 'react';
import type { AxiosInstance } from 'axios';

import { useIntegratedComponentContext } from '@components/integrated/shared/IntegratedComponentContext';
import type { IntegratedComponentContextValue } from '@components/integrated/shared/types';
import { claimflowApiClient } from '@/modules/claimflow/api/claimflowApiClient';

/** Module whose client/path serve the rules when no provider applies. */
export const FALLBACK_RULES_MODULE = 'claimflow';

/** The client + module path a rules request goes through. */
export interface RulesApiTarget {
  /** Authenticated axios client of the serving module. */
  apiClient: AxiosInstance;
  /** Module slug of the serving route group (`/<module>/component-rules`). */
  module: string;
}

/**
 * Derive the module token from a registered component id.
 *
 * Registered ids follow the `<module>.<name>` convention (e.g.
 * `claimflow.openCoverageCard` → `claimflow`). A degenerate id with no `.` is
 * treated as its own module (returns the id unchanged), which keeps the hooks
 * robust against ad-hoc ids without throwing.
 */
export function moduleOf(componentId: string): string {
  const dot = componentId.indexOf('.');
  return dot === -1 ? componentId : componentId.slice(0, dot);
}

/**
 * Pure resolution rule — see the file header.
 *
 * @param context         - The `IntegratedComponentProvider` value, or `null`
 *                          outside any provider.
 * @param componentModule - Module token of the component the request is about
 *                          (`moduleOf(componentId)`), or `undefined` for
 *                          module-wide requests (the inactive-rules map).
 */
export function resolveRulesApiTarget(
  context: IntegratedComponentContextValue | null,
  componentModule?: string,
): RulesApiTarget {
  if (
    context !== null &&
    context.module !== '' &&
    (componentModule === undefined || componentModule === context.module)
  ) {
    return { apiClient: context.apiClient, module: context.module };
  }
  return { apiClient: claimflowApiClient, module: FALLBACK_RULES_MODULE };
}

/** Base path of the component-rules routes for a resolved target. */
export function componentRulesPath(target: RulesApiTarget): string {
  return `/${target.module}/component-rules`;
}

/**
 * Hook form of {@link resolveRulesApiTarget}: reads the nearest
 * `IntegratedComponentProvider` and memoises the resolved target.
 */
export function useRulesApiTarget(componentModule?: string): RulesApiTarget {
  const context = useIntegratedComponentContext();
  return useMemo(
    () => resolveRulesApiTarget(context, componentModule),
    [context, componentModule],
  );
}
